import { FlexColumn, FlexRow, LabeledInput, MaterialIcon, useLoading } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useMemo, useState } from 'react';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../constants/app';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { postDevicesModel } from '../../services/deviceService';
import { DeviceModel } from '../../types/deviceModel';
import { DevicesBrandForm } from '../devicesBrands/DevicesBrandForm';
import { DeviceBrandSelect } from '../shared/selects/DeviceBrandSelect';
import StyledTooltip from '../ui/StyledTooltip';

type Props = {
  onCancel: () => void;
  afterSave: () => void;
  deviceBrandId?: string;
};
export function DeviceModelForm({ onCancel, afterSave, deviceBrandId }: Readonly<Props>) {
  const { css } = useStyles();
  const [editingDeviceModel, setEditingDeviceModel] = useState<DeviceModel>({
    _id: '',
    name: '',
    brand: {
      _id: '',
      name: '',
    },
  });
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const [createBrand, setCreateBrand] = useState(false);
  const [selectedDeviceBrandId, setSelectedDeviceBrandId] = useState<string>();

  const canSubmit = useMemo(() => {
    return !!editingDeviceModel.name && !!editingDeviceModel.brand._id;
  }, [editingDeviceModel]);

  const onInputChange = (value: string | undefined | null | {}, field: keyof DeviceModel) => {
    setEditingDeviceModel((prev) => ({ ...prev!, [field]: value }));
  };

  useEffect(() => {
    if (!deviceBrandId && !selectedDeviceBrandId) return;
    setSelectedDeviceBrandId(deviceBrandId);
    onInputChange({ _id: deviceBrandId }, 'brand');
  }, [deviceBrandId]);

  const onSave = async () => {
    startLoading();
    const { id, error } = await postDevicesModel(editingDeviceModel);
    if (!error && id) {
      afterSave();
      showPositiveFeedback(FEEDBACK.created(FEEDBACK_PREFIXES.deviceModel));
    } else {
      showFailFeedback(error || FEEDBACK.failedCreation(FEEDBACK_PREFIXES.deviceModel));
    }
    stopLoading();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
        width: '100%',
        flexDirection: 'row',
        alignItems: 'end',
      })}
    >
      <FlexColumn
        classNames={css({
          overflow: 'auto',
          width: '100%',
          height: '100%',
          flexDirection: 'row-reverse',
        })}
      >
        <LabeledInput
          label={`(Nuevo Modelo) Nombre:`}
          value={editingDeviceModel?.name}
          onChange={(value) => onInputChange(value, 'name')}
          required
        />
        {createBrand && (
          <FlexRow classNames={css({ width: '100%', alignItems: 'end' })}>
            <DevicesBrandForm
              afterSave={(brandId) => {
                setCreateBrand(false);
                onInputChange({ _id: brandId }, 'brand');
              }}
              onCancel={() => {
                setCreateBrand(false);
                onInputChange({ _id: editingDeviceModel.brand._id }, 'brand');
              }}
            />
          </FlexRow>
        )}
        {!createBrand && (
          <FlexRow
            classNames={css({ width: '100%', alignItems: 'end' })}
            gap={'8px'}
          >
            <DeviceBrandSelect
              selectedDeviceBrandId={editingDeviceModel?.brand._id}
              onChangeDeviceBrand={(brandId) => onInputChange({ _id: brandId }, 'brand')}
              isFilter={false}
              required
            />
            <StyledTooltip
              content={'Nueva'}
              showArrow={false}
            >
              <Button
                size="compact"
                shape="square"
                onClick={() => setCreateBrand(true)}
                kind="tertiary"
              >
                <MaterialIcon
                  name={'add'}
                  size="compact"
                />
              </Button>
            </StyledTooltip>
          </FlexRow>
        )}
      </FlexColumn>
      <FlexRow gap={0}>
        <StyledTooltip
          content={'Cancelar'}
          showArrow={false}
        >
          <Button
            size="compact"
            shape="square"
            onClick={onCancel}
            kind="tertiary"
          >
            <MaterialIcon name={'cancel'} />
          </Button>
        </StyledTooltip>
        <StyledTooltip
          content={'Guardar'}
          showArrow={false}
        >
          <Button
            size="compact"
            shape="square"
            onClick={onSave}
            isLoading={loading}
            disabled={!canSubmit}
            kind="tertiary"
          >
            <MaterialIcon name={'done'} />
          </Button>
        </StyledTooltip>
      </FlexRow>
    </FlexColumn>
  );
}
